import { House } from 'lucide-react';
import { AddToCartButton } from '@/components/orders/AddToCartButton';
import type { PropertyEntity } from '@/types/property.types';

interface PropertyPickerRowProps {
  property: PropertyEntity;
  onAddToCart: (property: PropertyEntity) => void;
  isAddingToCart: boolean;
  /** True when this property is already in the cart. */
  isInCart: boolean;
}

export function PropertyPickerRow({
  property,
  onAddToCart,
  isAddingToCart,
  isInCart,
}: PropertyPickerRowProps) {
  const cityStateZip = [
    property.city,
    property.state,
    property.zipCode,
  ]
    .filter(Boolean)
    .join(', ')
    .replace(/, ([^,]+)$/, ' $1'); // "Denver, CO 80205"

  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-card p-3">
      <div className="h-12 w-12 shrink-0 overflow-hidden rounded bg-muted">
        {property.logoUrl ? (
          <img
            src={property.logoUrl}
            alt={property.address1}
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center"
            aria-label="No Property Image"
          >
            <House
              className="h-6 w-6 text-muted-foreground"
              strokeWidth={1.5}
            />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{property.address1}</p>
        <p className="truncate text-xs text-muted-foreground">{cityStateZip}</p>
        {property.clientPropertyId && (
          <p className="truncate text-xs text-muted-foreground">
            <span className="font-medium">Property ID:</span>{' '}
            {property.clientPropertyId}
          </p>
        )}
      </div>
      <AddToCartButton
        size="sm"
        isAdding={isAddingToCart}
        isAdded={isInCart}
        addedLabel="Added"
        onAddToCart={() => onAddToCart(property)}
        // Lock width to the wider idle state so the button doesn't
        // visibly shrink when transitioning to "Added".
        className="min-w-[7.5rem]"
      />
    </div>
  );
}
